import { XTRAM_BOT_API_URL } from '../../env';

/**
 * POST /ask with SSE (text/event-stream). Same prompt/session contract as fetchSendMessage.
 * On failure after stream starts, calls onError with { status, message, data } compatible with callApi errors.
 */
export async function streamMessage({
  query,
  session_id,
  onFirstToken,
  onToken,
  onFinal,
  onError,
  signal,
}) {
  const url = `${XTRAM_BOT_API_URL}/ask`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'text/event-stream',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ question: query, session_id }),
    signal,
  });

  if (!res.ok) {
    let data = null;
    try {
      data = await res.json();
    } catch {
      // ignore
    }
    const err = {
      status: res.status,
      message: data?.error || res.statusText || 'Request failed',
      data: data || { error: res.statusText },
    };
    onError?.(err);
    throw err;
  }

  if (!res.body || !res.body.getReader) {
    const err = { status: 0, message: 'Streaming not supported in this browser', data: null };
    onError?.(err);
    throw err;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let firstTokenSent = false;

  const dispatchFrame = (eventName, dataLine) => {
    let payload = dataLine;
    try {
      payload = JSON.parse(dataLine);
    } catch {
      // keep raw string
    }

    if (eventName === 'token') {
      const raw = typeof payload === 'string' ? payload : String(payload ?? '');
      const chunk = raw.replace(/\[XTRAM_RESPONSE_FORMAT:(?:TEXT|HTML|MIXED)\]/gi, '');
      if (!chunk) return;
      if (!firstTokenSent) {
        firstTokenSent = true;
        onFirstToken?.();
      }
      onToken?.(chunk);
      return;
    }

    if (eventName === 'final') {
      onFinal?.(payload);
      return;
    }

    if (eventName === 'error') {
      const status = typeof payload === 'object' && payload && 'status_code' in payload ? payload.status_code : 502;
      const err = {
        status,
        message: typeof payload === 'object' && payload?.error ? payload.error : 'Stream error',
        data: typeof payload === 'object' && payload ? payload : { error: String(payload) },
      };
      onError?.(err);
      throw err;
    }

    if (eventName === 'end') {
      return;
    }
  };

  const processFrames = (text) => {
    const parts = text.split('\n\n');
    const complete = parts.slice(0, -1);
    const rest = parts[parts.length - 1];

    for (const frame of complete) {
      if (!frame.trim()) continue;
      let eventName = 'message';
      const dataLines = [];
      for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) {
          eventName = line.slice(6).trim();
        } else if (line.startsWith('data:')) {
          dataLines.push(line.slice(5).trim());
        }
      }
      const dataStr = dataLines.join('\n');
      if (dataStr) dispatchFrame(eventName, dataStr);
    }
    return rest;
  };

  try {
    let readDone = false;
    while (!readDone) {
      const { done, value } = await reader.read();
      readDone = done;
      if (value) buffer += decoder.decode(value, { stream: true });
      buffer = processFrames(buffer);
    }
    if (buffer.trim()) {
      processFrames(`${buffer}\n\n`);
    }
  } catch (e) {
    if (e?.status != null) throw e;
    const err = { status: null, message: e?.message || 'Stream read failed', data: null };
    onError?.(err);
    throw e;
  }
}
